import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../../data/context/app-context';
import { ChatMessageItem } from '../../../../../data/data/types';
import { DeleteConversationChatMessageItemDocument } from '../../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../intl/custom-formatted-message';
import { ModalPrompt } from '../../../../modal/modal-prompt';

type ChatMessageDeletePromptProps = {
  messageItem: ChatMessageItem;
  onDeleted?: (value: ChatMessageItem) => void;
  onClose?: () => void;
};

export function ChatMessageDeletePrompt({
  messageItem,
  onDeleted,
  onClose,
}: ChatMessageDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deleteConversationChatMessageItem] = useMutation(
    DeleteConversationChatMessageItemDocument
  );

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteConversationChatMessageItem({
        variables: {
          id: messageItem?.id ?? '',
        },
      });

      if (result.data?.messageDelete) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'conversation.chat-message-delete.success',
          }),
        });

        onDeleted?.(messageItem);

        onClose?.();
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    messageItem,
    deleteConversationChatMessageItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  return (
    <ModalPrompt
      testId="chat-message-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="conversation.chat-message-delete.content" />
      </p>
    </ModalPrompt>
  );
}
